import type { Survey, SurveySchema } from '@responsedock/types';
import type { SurveyResponseItem } from '../hooks/useSurveys';

type MockAnalytics = Record<string, unknown>;

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.getOwnPropertyNames(value).forEach((key) => {
      const nested = (value as Record<string, unknown>)[key];
      if (nested && typeof nested === 'object') deepFreeze(nested);
    });
  }
  return value;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function buildSchema(questions: Array<{ id: string; label: string; type: 'radio' | 'dropdown' | 'rating' | 'nps' | 'short_text' }>): SurveySchema {
  return {
    pages: [
      {
        id: 'mock_page_1',
        surveyVersionId: 'mock_version_1',
        title: { en: 'Feedback' },
        description: { en: 'Please share your experience.' },
        orderIndex: 0,
        isOptional: false,
        blocks: questions.map((q, idx) => ({
          id: `mock_block_${q.id}`,
          pageId: 'mock_page_1',
          type: 'question',
          orderIndex: idx,
          config: {},
          question: {
            id: q.id,
            blockId: `mock_block_${q.id}`,
            type: q.type,
            label: { en: q.label },
            description: null,
            required: true,
            config: {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
    followUpConfig: null,
    estimatedMinutes: 2,
    theme: null,
  };
}

const frozenMockSurveys: ReadonlyArray<Survey> = deepFreeze([
  {
    id: 'mock_survey_product_nps',
    workspaceId: 'mock_workspace',
    title: 'Product Experience Pulse',
    description: 'Quick weekly pulse for active users.',
    slug: 'product-experience-pulse',
    status: 'ONLINE',
    publicResultsEnabled: false,
    activeVersionId: 'mock_version_product_nps',
    defaultLanguage: 'en',
    supportedLanguages: ['en'],
    anonymityMode: 'ANONYMOUS',
    sourceTrackingMode: 'BASIC',
    duplicateRule: 'ALLOW_MULTIPLE',
    startsAt: null,
    endsAt: null,
    createdByUserId: 'mock_user',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    activeVersion: {
      id: 'mock_version_product_nps',
      surveyId: 'mock_survey_product_nps',
      versionNumber: 1,
      status: 'PUBLISHED',
      schema: buildSchema([
        { id: 'q_nps', label: 'How likely are you to recommend our product?', type: 'nps' },
        { id: 'q_reason', label: 'What is the main reason for your score?', type: 'short_text' },
      ]),
      publishedAt: new Date().toISOString(),
      createdByUserId: 'mock_user',
      createdAt: new Date().toISOString(),
    },
    stats: {
      views: 142,
      starts: 113,
      completions: 96,
      completionRate: 85,
      lastResponseAt: new Date().toISOString(),
    },
  },
  {
    id: 'mock_survey_event_feedback',
    workspaceId: 'mock_workspace',
    title: 'Event Feedback - Spring Meetup',
    description: 'Post-event quality survey for attendees.',
    slug: 'event-feedback-spring-meetup',
    status: 'ONLINE',
    publicResultsEnabled: false,
    activeVersionId: 'mock_version_event_feedback',
    defaultLanguage: 'en',
    supportedLanguages: ['en'],
    anonymityMode: 'IDENTIFIED',
    sourceTrackingMode: 'FULL',
    duplicateRule: 'ONE_PER_EXTERNAL_USER',
    startsAt: null,
    endsAt: null,
    createdByUserId: 'mock_user',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    activeVersion: {
      id: 'mock_version_event_feedback',
      surveyId: 'mock_survey_event_feedback',
      versionNumber: 2,
      status: 'PUBLISHED',
      schema: buildSchema([
        { id: 'q_overall', label: 'Overall event rating', type: 'rating' },
        { id: 'q_topic', label: 'Most useful topic', type: 'dropdown' },
        { id: 'q_comment', label: 'Additional feedback', type: 'short_text' },
      ]),
      publishedAt: new Date().toISOString(),
      createdByUserId: 'mock_user',
      createdAt: new Date().toISOString(),
    },
    stats: {
      views: 89,
      starts: 77,
      completions: 64,
      completionRate: 83,
      lastResponseAt: new Date().toISOString(),
    },
  },
  {
    id: 'mock_survey_checkout_cs',
    workspaceId: 'mock_workspace',
    title: 'Checkout Satisfaction Snapshot',
    description: 'Micro survey triggered after successful purchase.',
    slug: 'checkout-satisfaction-snapshot',
    status: 'ONLINE',
    publicResultsEnabled: false,
    activeVersionId: 'mock_version_checkout_cs',
    defaultLanguage: 'en',
    supportedLanguages: ['en'],
    anonymityMode: 'ANONYMOUS',
    sourceTrackingMode: 'BASIC',
    duplicateRule: 'ONE_PER_BROWSER',
    startsAt: null,
    endsAt: null,
    createdByUserId: 'mock_user',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    activeVersion: {
      id: 'mock_version_checkout_cs',
      surveyId: 'mock_survey_checkout_cs',
      versionNumber: 1,
      status: 'PUBLISHED',
      schema: buildSchema([
        { id: 'q_easy', label: 'How easy was checkout?', type: 'rating' },
        { id: 'q_speed', label: 'Checkout speed', type: 'radio' },
        { id: 'q_improve', label: 'What should we improve?', type: 'short_text' },
      ]),
      publishedAt: new Date().toISOString(),
      createdByUserId: 'mock_user',
      createdAt: new Date().toISOString(),
    },
    stats: {
      views: 301,
      starts: 219,
      completions: 181,
      completionRate: 83,
      lastResponseAt: new Date().toISOString(),
    },
  },
]);

const frozenAnalyticsBySurveyId: Readonly<Record<string, MockAnalytics>> = deepFreeze({
  mock_survey_product_nps: {
    total: 96,
    completed: 96,
    completionRate: 100,
    dropOffByPage: [
      { pageId: 'mock_page_1', title: { en: 'Feedback' }, started: 113, completed: 96, dropOff: 17, dropOffRate: 15 },
    ],
    sourceSegments: {
      channel: { in_app: 62, email: 28, qr: 6 },
      utmSource: { product: 62, lifecycle: 28, event: 6 },
      deviceType: { desktop: 57, mobile: 31, tablet: 8 },
    },
    responsesOverTime: [
      { date: '2026-05-13', total: 18, completed: 18 },
      { date: '2026-05-14', total: 14, completed: 14 },
      { date: '2026-05-15', total: 16, completed: 16 },
      { date: '2026-05-16', total: 15, completed: 15 },
      { date: '2026-05-17', total: 11, completed: 11 },
      { date: '2026-05-18', total: 10, completed: 10 },
      { date: '2026-05-19', total: 12, completed: 12 },
    ],
    questionSummaries: [
      {
        questionId: 'q_nps',
        label: 'How likely are you to recommend our product?',
        type: 'nps',
        answerCount: 96,
        summary: {
          score: 42,
          promoters: 58,
          passives: 26,
          detractors: 16,
          distribution: [
            { value: 6, count: 8, percent: 8 },
            { value: 7, count: 13, percent: 14 },
            { value: 8, count: 12, percent: 12 },
            { value: 9, count: 34, percent: 35 },
            { value: 10, count: 29, percent: 30 },
          ],
        },
      },
      {
        questionId: 'q_reason',
        label: 'What is the main reason for your score?',
        type: 'short_text',
        answerCount: 96,
        summary: {
          recentAnswers: [
            'Fast support and intuitive UI.',
            'Great value, but reporting can be deeper.',
            'Easy setup for our team.',
          ],
        },
      },
    ],
  },
  mock_survey_event_feedback: {
    total: 77,
    completed: 64,
    completionRate: 83,
    dropOffByPage: [
      { pageId: 'mock_page_1', title: { en: 'Feedback' }, started: 77, completed: 64, dropOff: 13, dropOffRate: 17 },
    ],
    sourceSegments: {
      channel: { qr: 38, email: 29, link: 10 },
      utmSource: { meetup: 38, newsletter: 29, partner: 10 },
      deviceType: { mobile: 48, desktop: 25, tablet: 4 },
    },
    responsesOverTime: [
      { date: '2026-05-10', total: 20, completed: 17 },
      { date: '2026-05-11', total: 24, completed: 20 },
      { date: '2026-05-12', total: 18, completed: 15 },
      { date: '2026-05-13', total: 15, completed: 12 },
    ],
    questionSummaries: [
      {
        questionId: 'q_overall',
        label: 'Overall event rating',
        type: 'rating',
        answerCount: 64,
        summary: {
          average: 4.3,
          distribution: [
            { value: 1, count: 1, percent: 2 },
            { value: 2, count: 3, percent: 5 },
            { value: 3, count: 9, percent: 14 },
            { value: 4, count: 22, percent: 34 },
            { value: 5, count: 29, percent: 45 },
          ],
        },
      },
      {
        questionId: 'q_topic',
        label: 'Most useful topic',
        type: 'dropdown',
        answerCount: 64,
        summary: {
          distribution: [
            { value: 'AI Workflows', count: 28, percent: 44 },
            { value: 'Survey Ops', count: 19, percent: 30 },
            { value: 'Product Analytics', count: 17, percent: 27 },
          ],
        },
      },
    ],
  },
  mock_survey_checkout_cs: {
    total: 219,
    completed: 181,
    completionRate: 83,
    dropOffByPage: [
      { pageId: 'mock_page_1', title: { en: 'Feedback' }, started: 219, completed: 181, dropOff: 38, dropOffRate: 17 },
    ],
    sourceSegments: {
      channel: { widget: 181, modal: 28, link: 10 },
      utmSource: { checkout: 181, receipts: 28, support: 10 },
      deviceType: { desktop: 126, mobile: 86, tablet: 7 },
    },
    responsesOverTime: [
      { date: '2026-05-13', total: 29, completed: 25 },
      { date: '2026-05-14', total: 34, completed: 28 },
      { date: '2026-05-15', total: 37, completed: 32 },
      { date: '2026-05-16', total: 25, completed: 19 },
      { date: '2026-05-17', total: 31, completed: 25 },
      { date: '2026-05-18', total: 30, completed: 26 },
      { date: '2026-05-19', total: 33, completed: 26 },
    ],
    questionSummaries: [
      {
        questionId: 'q_easy',
        label: 'How easy was checkout?',
        type: 'rating',
        answerCount: 181,
        summary: {
          average: 4.1,
          distribution: [
            { value: 1, count: 5, percent: 3 },
            { value: 2, count: 15, percent: 8 },
            { value: 3, count: 42, percent: 23 },
            { value: 4, count: 54, percent: 30 },
            { value: 5, count: 65, percent: 36 },
          ],
        },
      },
    ],
  },
});

const frozenResponsesBySurveyId: Readonly<Record<string, SurveyResponseItem[]>> = deepFreeze({
  mock_survey_product_nps: [
    {
      id: 'mock_resp_001',
      surveyId: 'mock_survey_product_nps',
      createdAt: new Date('2026-05-19T09:12:00Z').toISOString(),
      completedAt: new Date('2026-05-19T09:13:20Z').toISOString(),
      externalUserId: null,
      isAnonymous: true,
      answers: [
        { id: 'a1', questionId: 'q_nps', value: 9, createdAt: new Date('2026-05-19T09:12:45Z').toISOString() },
        { id: 'a2', questionId: 'q_reason', value: 'It saves us time every week.', createdAt: new Date('2026-05-19T09:13:10Z').toISOString() },
      ],
    },
    {
      id: 'mock_resp_002',
      surveyId: 'mock_survey_product_nps',
      createdAt: new Date('2026-05-18T13:02:00Z').toISOString(),
      completedAt: new Date('2026-05-18T13:03:15Z').toISOString(),
      externalUserId: null,
      isAnonymous: true,
      answers: [
        { id: 'a3', questionId: 'q_nps', value: 8, createdAt: new Date('2026-05-18T13:02:28Z').toISOString() },
        { id: 'a4', questionId: 'q_reason', value: 'Great UI, some filters missing.', createdAt: new Date('2026-05-18T13:03:00Z').toISOString() },
      ],
    },
  ],
  mock_survey_event_feedback: [
    {
      id: 'mock_resp_101',
      surveyId: 'mock_survey_event_feedback',
      createdAt: new Date('2026-05-12T18:45:00Z').toISOString(),
      completedAt: new Date('2026-05-12T18:46:40Z').toISOString(),
      externalUserId: 'attendee_44',
      isAnonymous: false,
      answers: [
        { id: 'a101', questionId: 'q_overall', value: 5, createdAt: new Date('2026-05-12T18:45:15Z').toISOString() },
        { id: 'a102', questionId: 'q_topic', value: 'AI Workflows', createdAt: new Date('2026-05-12T18:45:50Z').toISOString() },
        { id: 'a103', questionId: 'q_comment', value: 'Loved the practical examples.', createdAt: new Date('2026-05-12T18:46:30Z').toISOString() },
      ],
    },
  ],
  mock_survey_checkout_cs: [
    {
      id: 'mock_resp_201',
      surveyId: 'mock_survey_checkout_cs',
      createdAt: new Date('2026-05-19T16:20:00Z').toISOString(),
      completedAt: new Date('2026-05-19T16:20:55Z').toISOString(),
      externalUserId: null,
      isAnonymous: true,
      answers: [
        { id: 'a201', questionId: 'q_easy', value: 4, createdAt: new Date('2026-05-19T16:20:15Z').toISOString() },
        { id: 'a202', questionId: 'q_speed', value: 'Fast', createdAt: new Date('2026-05-19T16:20:30Z').toISOString() },
        { id: 'a203', questionId: 'q_improve', value: 'Show shipping estimate earlier.', createdAt: new Date('2026-05-19T16:20:50Z').toISOString() },
      ],
    },
  ],
});

export function isMockSurveyId(surveyId: string): boolean {
  return surveyId.startsWith('mock_survey_');
}

export function getMockSurveys(): Survey[] {
  return clone([...frozenMockSurveys]);
}

export function getMockSurveyById(surveyId: string): Survey | null {
  const found = frozenMockSurveys.find((s) => s.id === surveyId);
  return found ? clone(found) : null;
}

export function getMockAnalyticsBySurveyId(surveyId: string): MockAnalytics | null {
  const analytics = frozenAnalyticsBySurveyId[surveyId];
  return analytics ? clone(analytics) : null;
}

export function getMockResponsesBySurveyId(surveyId: string): SurveyResponseItem[] {
  const responses = frozenResponsesBySurveyId[surveyId] ?? [];
  return clone(responses);
}
